import { ReactElement } from "react";
import {
	IntegrationFlowValidationResultGeneralSampleModel,
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import { DateString, FormItem } from "client/Components";
import { TimeZone } from "client/DateAndTime/timezones";
import { IInputAttachable, IObjectInputAttachable } from "client/Forms/attachable";
import { isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { GeneralMappingStepConfigurationFormData, ResultMapperStyledFieldFormData } from "./common";
import { ResultMapperFormResult } from "./ResultMapperFormResult";
import { ExpressionTextInput } from "./ExpressionTextInput";
import { GeneralResultFieldWithColorActions } from "./GeneralResultFieldWithColorAction";
import { ColorSelector } from "./ColorSelector";

export type GeneralFormProps = {
	attachable: IObjectInputAttachable<GeneralMappingStepConfigurationFormData>;
	generalSample: OptionalModel<IntegrationFlowValidationResultGeneralSampleModel> | null;
	isReadOnly: boolean;
	timeZone: TimeZone;
};
export const GeneralForm = (props: GeneralFormProps): ReactElement => {
	const {
		attachable,
		generalSample,
		isReadOnly,
		timeZone
	} = props;

	const linkExpressionHandler: IInputAttachable<string> = attachable.for("linkExpression");
	const titleHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("title");
	const dateHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("date");
	const subtitle1Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle1");
	const subtitle2Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle2");
	const subtitle3Handler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("subtitle3");
	const descriptionHandler: IObjectInputAttachable<ResultMapperStyledFieldFormData> = attachable.for("description");
	const hasResult: boolean = isNotNil(generalSample) && generalSample.isSet;

	const generalSampleValue: IntegrationFlowValidationResultGeneralSampleModel | null = isNotNil(generalSample)
		&& generalSample.isSet
			? generalSample.value
			: null;

	return (
		<>
			<FormItem
				label={localize("_Title")}
				isRequired
			>
				<GeneralResultFieldWithColorActions
					attachable={titleHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={generalSampleValue?.title}
				/>
			</FormItem>
			{titleHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									generalSampleValue?.title
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_Link")}
				isRequired
			>
				<ExpressionTextInput
					isReadOnly={isReadOnly}
					attachable={linkExpressionHandler}
				/>
				<ResultMapperFormResult
					attachable={linkExpressionHandler}
					hasResult={hasResult}
					resultValue={generalSampleValue?.link}
				/>
			</FormItem>
			<FormItem
				label={localize("_DateCreatedModified")}
			>
				<GeneralResultFieldWithColorActions
					attachable={dateHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={generalSampleValue?.dateTime}
				>
					{generalSampleValue?.dateTime?.value && (
						<DateString
							value={generalSampleValue?.dateTime.value}
							timezone={timeZone}
							format="RelativeDayWithTime"
						/>
					)}
				</GeneralResultFieldWithColorActions>
			</FormItem>
			{dateHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									generalSampleValue?.dateTime
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "1"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={subtitle1Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={generalSampleValue?.subTitle1}
				/>
			</FormItem>
			{subtitle1Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									generalSampleValue?.subTitle1
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "2"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={subtitle2Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={generalSampleValue?.subTitle2}
				/>
			</FormItem>
			{subtitle2Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									generalSampleValue?.subTitle2
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={{
					template: localize("_SubTitleN"),
					replacements: [
						{
							what: "{{N}}",
							filler: "3"
						}
					]
				}}
			>
				<GeneralResultFieldWithColorActions
					attachable={subtitle3Handler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={generalSampleValue?.subTitle3}
				/>
			</FormItem>
			{subtitle3Handler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									generalSampleValue?.subTitle3
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
			<FormItem
				label={localize("_Description")}
			>
				<GeneralResultFieldWithColorActions
					attachable={descriptionHandler}
					isReadOnly={isReadOnly}
					hasResult={hasResult}
					resultField={generalSampleValue?.description}
				/>
			</FormItem>
			{descriptionHandler.when(
				x => x.shouldIncludeColors,
				(attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>): ReactElement => {
					return (
						<FormItem>
							<ColorSelector
								attachable={attachable}
								isReadOnly={isReadOnly}
								activeConditionIndex={
									generalSampleValue?.description
										?.colorConditionIndex
								}
							/>
						</FormItem>
					);
				}
			)}
		</>
	);
};
GeneralForm.displayName = "GeneralForm";